/**
 * Entry format v1 — the shape of `resolver_entries.entry`.
 *
 * This is the denormalised read model (§3.3): one JSON payload per GTIN, written by
 * the API inside the same transaction as the link edit, carried by the outbox, and
 * mirrored to every resolver node's SQLite file. It is the only thing the request
 * path reads, so it is the contract between the writer and the resolver — three
 * codebases encoding one shape, which is why it is a schema and not an interface.
 *
 * ## What this schema is for, and where it runs
 *
 * **Not on the hot path.** Validation happens where entries are *written* — the API
 * writer, the outbox poller applying a row to the mirror, the seed script, tests. A
 * scan reads a row the poller already accepted; re-validating per scan would spend
 * milliseconds re-deciding a question answered at write time, on the one code path
 * whose budget is measured in microseconds.
 *
 * That division is what lets an invariant live in the schema and still be free at
 * request time: the per-grain default rules below are checked once, where entries are
 * written, and cost a scan nothing.
 *
 * **The 500-link cap used to be the example given here and is no longer in this file**
 * (amended 2026-08-06, R1). It counts *brand-created* rows now — a recall's
 * materialised links are exempt, because §3.8 writes two per affected grain and
 * invariant 3 forbids a safety action being refused by a budget. A schema over a
 * payload cannot tell one row's provenance from another's without reading `recallId`
 * and thereby enforcing a product policy, so the cap lives in `packages/db`'s builder
 * and what remains here is {@link MAX_ENTRY_LINKS}, a defensive bound on a payload
 * assumed malformed.
 *
 * ## Versioning
 *
 * `version` is a literal, so a v2 payload does not parse as a v1 — that refusal is
 * what `/readyz` gates on (§10.1). A node whose mirror carries a different entry
 * format must rebuild rather than serve: reading a v1 payload with v2 assumptions
 * produces a plausible wrong answer, which is worse than a cold start.
 *
 * ## Why there is no top-level `default`
 *
 * §4's abridged sketch shows `entry` as `{tenant, default, links}`. There is no
 * `default` field here, deliberately. The default link is **not a property of the
 * entry** — it is a function of the request's qualifiers, resolved by a shadowing
 * pass over the `isDefault` subset at merge time (§3.5). A plain GTIN scan and a
 * LOT42 scan of the same entry can have different defaults, so any single stored
 * value would be right for one and wrong for the other.
 *
 * The 2026-08-04 ruling states the model exactly: *"'Default' is stored as a
 * property of a link so the merge stays a pure function over the entry, but it is
 * conceptually a per-grain slot."* Storing a derived copy beside the links it is
 * derived from would give us two representations of one fact that a bad write could
 * put out of step — and the thing they would disagree about is where a camera scan
 * of a recalled unit lands.
 *
 * What survives from that sketch is the *guarantee*, encoded below as a schema rule
 * instead of a field: at most one default per grain per content-negotiation variant,
 * and the GTIN grain is always occupied.
 */
import { checkAIValue } from "@packagepage/gs1";
import { z } from "zod";
import { BILLING_STATES, TIERS } from "./tiers.js";
/**
 * Version of the denormalised entry payload mirrored to resolver nodes.
 *
 * Bump on any change to the entry JSON. Spec §3.3, §10.1.
 */
export const ENTRY_FORMAT_VERSION = 1;
/**
 * Hard cap on **brand-created** links per GTIN — rows with `recall_id IS NULL`
 * (§15.7, ruled 2026-08-03; amended 2026-08-06, R1). The 501st such write fails with a
 * clear API error rather than being silently dropped.
 *
 * **The unit is the point of the name**, per c22: this counts links a brand made, in
 * one organisation, for one GTIN. It does not count rows the recall flow materialised,
 * and the rename from `MAX_LINKS_PER_GTIN` is deliberate — the old name was true when
 * every row was a brand's and became a lie the moment §3.8 wrote rows of its own.
 *
 * **Why it had to stop counting everything.** §3.8 writes two links per affected grain,
 * and invariant 3 says a safety action is never gated — no tier, no billing state, no
 * throttle. A GTIN sitting at 499 could not accept a recall: refused, by a cap written
 * for an unrelated purpose. Counting brand rows only means a recall is always
 * affordable, and the cap goes on meaning exactly what §15.7 wanted it to mean — a
 * brand's own link budget.
 *
 * Trivial to raise, painful to lower: lowering it would orphan links that already
 * resolve for printed codes.
 */
export const MAX_BRAND_LINKS_PER_GTIN = 500;
/**
 * The entry format's defensive ceiling on `links[]` — **not a product limit.**
 *
 * This is the bound past which a payload is assumed malformed, and it is deliberately
 * far above {@link MAX_BRAND_LINKS_PER_GTIN} so that the two are never confused for
 * each other. A brand meets the cap; nothing legitimate meets this.
 *
 * It exists because the brand cap moved out of this schema (R1) and something still has
 * to refuse a payload that is obviously wrong before a resolver node stores it. The
 * product number now lives in `packages/db`'s builder, which is what keeps §15.7's
 * *"trivial to raise later"* true: raising the cap is a constant in one place, where
 * raising a bound the whole fleet enforces would be a deploy.
 *
 * **A node that meets this refuses the entry, counts it in `rejected`, logs it and
 * advances `last_seq`** — it keeps serving the payload it already holds, so the failure
 * is staleness for one GTIN with an alert behind it, never a wrong answer. That is also
 * why raising this value later is resolver-before-writer: an older node would refuse an
 * entry a newer writer considers legal.
 */
export const MAX_ENTRY_LINKS = 1000;
/**
 * A qualifier value, validated against its AI's real GS1 format rules rather than a
 * length check — `packages/gs1` already owns those, and a lot code that fails AI 10's
 * charset is a code no scanner will ever produce.
 */
const qualifierValue = (ai) => z.string().superRefine((value, ctx) => {
    const error = checkAIValue(ai, value);
    if (error !== null)
        ctx.addIssue({ code: "custom", message: error.message });
});
/**
 * A CURIE: `gs1:pip`, or a tenant's own namespace at Growth+ (§4).
 *
 * Compared exactly and case-sensitively everywhere downstream, so the shape is
 * pinned here rather than being normalised later.
 */
const curie = z
    .string()
    .regex(/^[a-z][A-Za-z0-9]*:[a-z][A-Za-z0-9]*$/, "expected a CURIE such as gs1:pip");
/**
 * An http(s) URL. Plain `http` stays legal because local development and the compose
 * stack serve over it; every production destination is https.
 */
const httpUrl = z.url().refine((value) => /^https?:\/\//i.test(value), {
    message: "expected an http(s) URL",
});
/** A BCP 47 language tag, structurally — `en`, `en-GB`, `zh-Hant-TW`. */
const languageTag = z
    .string()
    .regex(/^[A-Za-z]{2,8}(-[A-Za-z0-9]{1,8})*$/, "expected a BCP 47 language tag");
/** An IANA media type, structurally — `text/html`, `application/pdf`. */
const mediaType = z
    .string()
    .regex(/^[\w.+-]+\/[\w.+-]+$/, "expected a media type such as text/html");
/**
 * One link in the entry.
 *
 * The field set matches `packages/gs1`'s `LinkRecord` exactly, and a compile-time
 * assertion below holds it there: the resolver hands `entry.links` straight to
 * `mergeLinkset` with no adapter and no cast, so a field renamed on one side has to
 * fail the build rather than resolve to `undefined` at 2 a.m.
 *
 * Optional fields use zod's `.exactOptional()` rather than `.optional()`. The repo
 * compiles with `exactOptionalPropertyTypes`, under which `{cpv?: string | undefined}`
 * — what `.optional()` infers — is not assignable to `LinkRecord`'s `{cpv?: string}`.
 * It also matches the runtime truth: these payloads arrive as JSON, which cannot
 * carry `undefined`, so a key is either absent or a value.
 */
export const entryLinkSchema = z
    .object({
    linkType: curie,
    href: httpUrl,
    title: z.string().min(1).exactOptional(),
    /** Language selection happens after the merge, over the survivors (§3.5). */
    hreflang: z.array(languageTag).min(1).exactOptional(),
    mediaType: mediaType.exactOptional(),
    // The grain. Absent means "not defined at this level", which is what makes a
    // link inheritable by finer requests (§3.5).
    cpv: qualifierValue("22").exactOptional(),
    lot: qualifierValue("10").exactOptional(),
    serial: qualifierValue("21").exactOptional(),
    active: z.boolean(),
    /**
     * Does this link occupy its grain's default slot?
     *
     * The only mover of plain scans. A finer-grained link never captures the default
     * by shadowing its own type — it must be flagged (ruled 2026-08-04). This is the
     * flag a recall's second link carries (§3.8).
     */
    isDefault: z.boolean(),
    /** Epoch milliseconds, inclusive lower bound. */
    validFrom: z.int().nonnegative().exactOptional(),
    /** Epoch milliseconds, **exclusive** upper bound — see gs1's `withinValidity`. */
    validUntil: z.int().nonnegative().exactOptional(),
    /**
     * The recall that materialised this link, if one did (§3.8).
     *
     * **Additive-optional, and deliberately not a version bump** — see the versioning
     * note above. An older resolver node's non-strict parse drops the key and simply
     * does not stamp, which is degradation rather than a wrong answer; the version
     * literal exists to refuse *shape* incompatibility, where reading a payload with
     * the wrong assumptions produces something plausible and false.
     *
     * ## Why the entry has to carry this at all
     *
     * §3.8 said *"the merge already knows whether a scan matched, so it stamps
     * `scan_events.recall_id` on matches"*. It did not: this schema had no recall
     * field, and `drizzle/0010` grants `resolver_svc` nothing on `recalls` or `links`
     * — deliberately, because §3.8 is not a resolver code path. **A resolver that
     * cannot see which link a recall materialised cannot stamp anything.** The fact
     * therefore has to travel the one road the resolver reads, which is this payload.
     *
     * Written by the entry builder; consumed by the resolver's scan capture, which
     * stamps when *any* post-`applies()` candidate carries one — not merely the link
     * finally chosen, because a `?linkType=gs1:pip` request for a recalled lot has
     * still matched the scope, and that is exactly the fact §3.8's alarm counts.
     */
    recallId: z.uuid().exactOptional(),
})
    .refine((link) => link.validFrom === undefined ||
    link.validUntil === undefined ||
    link.validUntil > link.validFrom, { message: "validUntil must be after validFrom", path: ["validUntil"] });
/**
 * Precomputed entitlement, carried in the entry so the hot path never calls billing
 * (§3.6). A worker flips these through the outbox like any other change.
 */
export const entryTenantSchema = z.object({
    status: z.enum(BILLING_STATES),
    plan: z.enum(TIERS),
    /** Whether the tenant's own PIP destination is used, or the platform's. */
    customPip: z.boolean(),
});
/** The qualifier tuple a link is defined at — its grain, as a comparable key. */
const grainKey = (link) => JSON.stringify([link.cpv ?? null, link.lot ?? null, link.serial ?? null]);
/**
 * What distinguishes two defaults at the same grain: the metadata content
 * negotiation would choose between. Two defaults identical on both are the data
 * defect §3.5 forbids — the merge cannot resolve them, so nothing downstream can.
 */
const variantKey = (link) => JSON.stringify([[...(link.hreflang ?? [])].sort(), link.mediaType ?? null]);
/**
 * Is this link defined at the GTIN grain — no qualifiers, inherited by everything?
 *
 * **Exported since c81**, where the consumer page reads the GTIN-grain default links to
 * decide the page's default *content* language. That is the plan's *"the product's declared
 * default"*, and it is declared here rather than in a column — see `apps/pip`'s
 * `lib/language.ts` for why a `products.default_lang` would be a defect rather than a fix.
 *
 * Exported rather than respelled for the reason `languageMatches` was: "no qualifiers" is a
 * three-way `undefined` check that reads as obvious and is wrong in one direction if a
 * fourth qualifier is ever added, and a copy in `apps/pip` would not move when this one did.
 */
export const isGtinGrain = (link) => link.cpv === undefined && link.lot === undefined && link.serial === undefined;
/**
 * The whole payload.
 *
 * `tombstone` is required rather than defaulted. A truncated or half-written payload
 * that omits it would otherwise read as "not tombstoned", which is the direction that
 * fails badly: a deleted product would resolve as live.
 */
export const resolverEntrySchema = z
    .object({
    version: z.literal(ENTRY_FORMAT_VERSION),
    tenant: entryTenantSchema,
    /**
     * The defensive ceiling, not the product cap — see {@link MAX_ENTRY_LINKS}. The
     * 500-brand-link rule (§15.7) is enforced by the builder in `packages/db`, because
     * a recall's rows are exempt from it and this schema cannot tell one row's
     * provenance from another's without reading `recallId`, which would make the format
     * enforce a product policy.
     */
    links: z.array(entryLinkSchema).max(MAX_ENTRY_LINKS),
    /**
     * The platform-hosted destination for this GTIN — a `pkgedge.com` page rendered
     * from last-known product data.
     *
     * Carried per entry rather than derived from a hostname the resolver would have
     * to be configured with, so degraded routing needs nothing beyond the mirror. It
     * serves the whole degradation ladder (§3.4, §3.6): the interstitial-branded page
     * for a `degraded` tenant, PIP-lite for a `churned` one, and the "no longer
     * available" page for a tombstone. One URL, because the destination decides what
     * to render — the resolver only redirects, and giving it three fields to choose
     * between would put product copy in the hot path.
     */
    platformFallbackUrl: httpUrl,
    /** Known-then-deleted product: serve the platform notice, never a 404 (§3.4). */
    tombstone: z.boolean(),
})
    .superRefine((entry, ctx) => {
    // Rule 1 — one default per grain, modulo content-negotiation variants (§3.5).
    // "Default" is conceptually a per-grain slot with at most one occupant; two
    // occupants that content negotiation cannot tell apart make the plain-scan
    // destination undecidable, and the merge deliberately does not guess.
    const seen = new Map();
    entry.links.forEach((link, index) => {
        if (!link.isDefault)
            return;
        const key = `${grainKey(link)}|${variantKey(link)}`;
        const first = seen.get(key);
        if (first === undefined) {
            seen.set(key, index);
            return;
        }
        ctx.addIssue({
            code: "custom",
            path: ["links", index],
            message: `two default links at the same grain with the same hreflang and media type ` +
                `(the other is links[${first}]) — a grain has one default slot, and content ` +
                `negotiation cannot choose between these`,
        });
    });
    // Rule 2 — the GTIN grain is always occupied (ruled 2026-08-04). Every finer
    // grain is optional; this one is not, because a plain scan of a GTIN with no
    // qualifiers must land somewhere. A tombstoned entry is the exception: its
    // destination is the platform notice, not a link.
    if (!entry.tombstone && !entry.links.some((l) => l.isDefault && isGtinGrain(l))) {
        ctx.addIssue({
            code: "custom",
            path: ["links"],
            message: "no default link at the GTIN grain — every non-tombstoned entry has exactly " +
                "one, or a plain camera scan has nowhere to go",
        });
    }
});
//# sourceMappingURL=entry.js.map